import { X } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { PlatformLogRow } from "@/components/platform-log-row";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PLATFORM_LOG_PAGE_SIZE,
  usePlatformLogAdminsQuery,
  usePlatformLogsQuery,
} from "@/features/api";
import { PLATFORM_ACTION_LABELS } from "@/lib/format";
import { cn } from "@/lib/utils";

const ALL = "all";

/** Filtreler URL'de: `adminId`, `action`, `organizerId` (organizatör detayındaki "Tümünü gör"), `page`. */
export function PlatformLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const adminId = searchParams.get("adminId") ?? "";
  const action = searchParams.get("action") ?? "";
  const organizerId = searchParams.get("organizerId") ?? "";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const admins = usePlatformLogAdminsQuery();
  const { data, isLoading, isFetching, error, refetch } = usePlatformLogsQuery({
    adminId,
    action,
    organizerId,
    page,
  });

  function update(key: "adminId" | "action" | "organizerId" | "page", value: string) {
    const params = new URLSearchParams(searchParams);
    if (!value || value === ALL || (key === "page" && value === "1")) params.delete(key);
    else params.set(key, value);
    if (key !== "page") params.delete("page");
    setSearchParams(params, { replace: true });
  }

  const pageCount = data ? Math.max(1, Math.ceil(data.total / PLATFORM_LOG_PAGE_SIZE)) : 1;
  const organizerName =
    data?.items.find((i) => i.targetOrganizerId === organizerId)?.targetOrganizerName ??
    "Seçili organizatör";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Platform kayıtları</h1>
        <p className="text-sm text-muted-foreground">
          Admin'lerin yaptığı tüm işlemler: giriş, ekip, ücret, askıya alma, ödeme, destek oturumu,
          rapor. Organizatörler bu ekranı görmez; kendi kayıtlarında "TicketNjoy" görürler.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={adminId || ALL} onValueChange={(v) => update("adminId", v)}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tüm admin'ler</SelectItem>
            {admins.data?.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={action || ALL} onValueChange={(v) => update("action", v)}>
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tüm işlemler</SelectItem>
            {Object.entries(PLATFORM_ACTION_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {organizerId && (
          <Button variant="outline" size="sm" onClick={() => update("organizerId", "")}>
            {organizerName}
            <X className="size-4" />
          </Button>
        )}
      </div>

      <Card>
        <CardContent>
          {error ? (
            <QueryError message="Kayıtlar yüklenemedi." onRetry={() => refetch()} retrying={isFetching} />
          ) : isLoading || !data ? (
            <p className="text-sm text-muted-foreground">Yükleniyor...</p>
          ) : data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Kayıt bulunamadı.</p>
          ) : (
            <ul className={cn("divide-y text-sm", isFetching && "opacity-60")}>
              {data.items.map((l) => (
                <PlatformLogRow
                  key={l.id}
                  action={l.action}
                  actorEmail={l.actorEmail}
                  diff={l.diff}
                  createdAt={l.createdAt}
                  ipAddress={l.ipAddress}
                  organizer={
                    l.targetOrganizerId ? { id: l.targetOrganizerId, name: l.targetOrganizerName } : null
                  }
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => update("page", String(page - 1))}
          >
            Önceki
          </Button>
          <span className="text-muted-foreground">
            {page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => update("page", String(page + 1))}
          >
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
}
